import React from "react";

import {
    FeaturedRichTextBlock as FeaturedValue,
    GoodRichTextBlock,
} from "@reactivated";

import { RichTextBlock as BaseRichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./rich-text.module.scss";

type TerminalMargin = "both" | "none" | "firstChild" | "lastChild";

interface Props extends React.ComponentProps<typeof BaseRichTextBlock> {
    /**
     * Usually we want the margin-top of the rich text's first child and the
     * margin-bottom of the rich text's last child to be trimmed so they don't
     * affect the layout. But there are some cases where it's useful, so here
     * are a few ways out if you need them.
     */
    trimTerminalMargins?: TerminalMargin;
    headingSize?: "large" | "extraLarge" | null;
    textAlignment?: GoodRichTextBlock["text_alignment"];
    /**
     * There are multiple spots on the site where we want headings to take up
     * the full width alloted to the rich text but we want the paragraph width
     * to be capped at something less.
     */
    constrainParagraphWidth?: "half" | "two-thirds" | null;
}

/**
 * For most use cases, you want to use this, not RichTextBlock
 */
export function RichText({
    trimTerminalMargins = "both",
    headingSize,
    textAlignment,
    constrainParagraphWidth = null,
    ...props
}: Props) {
    return (
        <BaseRichTextBlock
            {...props}
            className={concatClassNames([
                styles.richText,
                headingSize ? styles[headingSize] : "",
                styles[trimTerminalMargins],
                styles[textAlignment ?? "left"],
                props.className,
                constrainParagraphWidth === "half" ? styles.constrainHalf : "",
                constrainParagraphWidth === "two-thirds"
                    ? styles.constrainTwoThirds
                    : "",
                constrainParagraphWidth && textAlignment === "center"
                    ? styles.constrainCenter
                    : "",
            ])}
        />
    );
}

/**
 * The way this has panned out is: the actual CMS block will probably only be
 * used for larger blocks of text like blog posts or legalese. So it gets its
 * own styles with that use case in mind
 */
export const RichTextBlock = ({ value }: { value: GoodRichTextBlock }) => {
    if (!value.content) return <></>;

    return (
        <RichText
            textAlignment={value.text_alignment}
            value={value.content}
            className={styles.richTextBlock}
        />
    );
};

export const FeaturedRichTextBlock = ({ value }: { value: FeaturedValue }) => {
    if (!value.content) return <></>;

    return (
        <RichText
            textAlignment="center"
            headingSize={value.heading_size}
            value={value.content}
            constrainParagraphWidth="half"
        />
    );
};
